import {
  Component,
  ChangeDetectionStrategy,
  input,
  computed,
  signal,
} from '@angular/core';
import { IYuzBbox, IOrnekResim } from '@/src/app/shared/interfaces/yuz.interfaces';
import {
  bboxCropStili,
  normalizeBbox,
} from '@/src/app/shared/utils/bbox.utils';
import {
  AVATAR_GRADYANLARI,
  isimGradyanIndex,
  cinsiyetGradyani,
} from '@/src/app/shared/utils/gradient-tokens';

export type AvatarSekil = 'circle' | 'square' | 'rounded';
type AvatarMod = 'crop' | 'ornekCrop' | 'resim' | 'harf';

@Component({
  selector: 'universal-avatar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (mod() === 'crop') {
      <div class="ua" [style]="cropStil()"></div>
    } @else if (mod() === 'ornekCrop') {
      <div class="ua" [style]="ornekCropStil()"></div>
    } @else if (mod() === 'resim') {
      <div class="ua" [style]="boyutStil()">
        <img
          [src]="resimUrl()"
          (error)="resimHatasi()"
          (load)="resimYuklendi.set(true)"
          [style.display]="resimYuklendi() ? 'block' : 'none'"
          [style.width.px]="boyut()"
          [style.height.px]="boyut()"
          style="object-fit:cover;"
        />
        @if (!resimYuklendi()) {
          <div class="ua ua-harf" [style]="harfStil()">{{ harfler() }}</div>
        }
      </div>
    } @else {
      <div class="ua ua-harf" [style]="harfStil()">
        {{ harfler() }}
      </div>
    }
  `,
  styles: [`
    :host { display: inline-block; vertical-align: middle; }

    .ua {
      overflow: hidden;
      background-repeat: no-repeat;
      flex-shrink: 0;
    }

    .ua-harf {
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-weight: 700;
      letter-spacing: 0.01em;
      line-height: 1;
      user-select: none;
      text-shadow: 0 1px 3px rgba(0,0,0,0.18);
    }

    img { border-radius: inherit; }
  `],
})
export class UniversalAvatarComponent {
  // ── Temel Girdiler ──────────────────────────────────────────────────
  /** Profil resmi URL'si */
  readonly resimUrl = input('');
  /** Kisi adi (harf avatar + gradient hash icin) */
  readonly isim = input('');
  /** Cinsiyet: 'K'|'F'|'E'|'M' */
  readonly cinsiyet = input('');
  /** Avatar pixel boyutu */
  readonly boyut = input(48);
  /** Avatar sekli */
  readonly sekil = input<AvatarSekil>('circle');

  // ── Yuz Crop Modu ──────────────────────────────────────────────────
  /** Crop bounding box koordinatlari (resimUrl ile birlikte) */
  readonly bbox = input<IYuzBbox | undefined>(undefined);

  // ── Ornek Resim Modu (yuz-tanima) ─────────────────────────────────
  /** Ornek resim + bbox verisi (CSS crop) */
  readonly ornekResim = input<IOrnekResim | undefined>(undefined);

  // ── Custom URL Builder ─────────────────────────────────────────────
  /** ResimId'den URL uretme fonksiyonu (yuz-tanima API icin) */
  readonly resimUrlFn = input<((resimId: string, boyut: string) => string) | undefined>(undefined);

  // ── Dahili State ───────────────────────────────────────────────────
  readonly resimYuklendi = signal(false);
  private readonly resimHatasiVar = signal(false);

  // ── Computed: Aktif Mod ────────────────────────────────────────────
  readonly mod = computed<AvatarMod>(() => {
    // 1. resimUrl + bbox -> CSS crop
    if (this.resimUrl() && this.bbox()?.x != null) return 'crop';
    // 2. ornekResim -> ornek crop
    const ornek = this.ornekResim();
    if (ornek?.bbox && ornek.bbox.x != null) return 'ornekCrop';
    // 3. resimUrl (bbox yok) -> duz resim
    if (this.resimUrl() && !this.resimHatasiVar()) return 'resim';
    // 4. Fallback -> harf
    return 'harf';
  });

  // ── Computed: Border Radius ────────────────────────────────────────
  private readonly borderRadius = computed(() => {
    const s = this.sekil();
    if (s === 'circle') return '50%';
    if (s === 'rounded') return '8px';
    return '0';
  });

  // ── Computed: Harfler ──────────────────────────────────────────────
  readonly harfler = computed(() => {
    const s = this.isim()?.trim();
    if (!s) return '?';
    const parcalar = s.split(/\s+/);
    if (parcalar.length >= 2) {
      return ((parcalar[0]?.[0] || '') + (parcalar[parcalar.length - 1]?.[0] || '')).toUpperCase();
    }
    return (parcalar[0]?.[0] || '?').toUpperCase();
  });

  // ── Computed: Gradient ─────────────────────────────────────────────
  private readonly gradyan = computed(() => {
    const c = this.cinsiyet();
    if (c) return cinsiyetGradyani(c);
    const isim = this.isim()?.trim() || '?';
    const idx = isimGradyanIndex(isim);
    const [r1, r2] = AVATAR_GRADYANLARI[idx];
    return `linear-gradient(135deg, ${r1}, ${r2})`;
  });

  // ── Computed: Font Boyutu ──────────────────────────────────────────
  private readonly fontBoyutu = computed(() => {
    return Math.round(this.boyut() * 0.4);
  });

  // ── Computed Stiller ───────────────────────────────────────────────

  /** crop modu: resimUrl + bbox ile bboxCropStili */
  readonly cropStil = computed(() => {
    const url = this.resimUrl();
    const bb = this.bbox();
    if (!url || !bb) return '';
    const yuvarlak = this.sekil() === 'circle';
    return bboxCropStili(url, bb, this.boyut(), yuvarlak);
  });

  /** ornekCrop modu: ornekResim ile CSS crop */
  readonly ornekCropStil = computed(() => {
    const ornek = this.ornekResim();
    if (!ornek?.bbox) return '';

    const px = this.boyut();
    const fn = this.resimUrlFn();
    const kaynakBoyut = Math.max(px * 6, 480);
    const url = fn
      ? fn(ornek.resimId, `${kaynakBoyut}x${kaynakBoyut}`)
      : `/api/resim/${ornek.resimId}`;

    const { x, y, w, h } = normalizeBbox(ornek.bbox);
    if (w <= 0 || h <= 0) return `width:${px}px;height:${px}px;background:#333;border-radius:${this.borderRadius()};`;

    const bgSizeX = (1 / w) * 100;
    const bgSizeY = (1 / h) * 100;
    const bgPosX = w >= 1 ? 50 : (x / (1 - w)) * 100;
    const bgPosY = h >= 1 ? 50 : (y / (1 - h)) * 100;

    return `width:${px}px;height:${px}px;` +
      `background-image:url('${url}');` +
      `background-size:${bgSizeX}% ${bgSizeY}%;` +
      `background-position:${bgPosX}% ${bgPosY}%;` +
      `background-repeat:no-repeat;` +
      `background-color:#f0f0f0;` +
      `border-radius:${this.borderRadius()};`;
  });

  /** resim modu: wrapper div boyut + border-radius */
  readonly boyutStil = computed(() => {
    const px = this.boyut();
    return `width:${px}px;height:${px}px;border-radius:${this.borderRadius()};overflow:hidden;position:relative;`;
  });

  /** harf modu: gradient bg + font boyutu */
  readonly harfStil = computed(() => {
    const px = this.boyut();
    return `width:${px}px;height:${px}px;` +
      `background:${this.gradyan()};` +
      `border-radius:${this.borderRadius()};` +
      `font-size:${this.fontBoyutu()}px;`;
  });

  // ── Event Handlers ─────────────────────────────────────────────────

  resimHatasi(): void {
    this.resimHatasiVar.set(true);
    this.resimYuklendi.set(false);
  }
}
